// Earth shader, photorealism batch 1
//   E1  Ocean: water column, painted water and Cox-Munk sun glint (earthocean.js)
//   E2  Tangent-space normal map via derivative TBN (thetenthplanet.de)
//   E3  Cloud UV drift + cloud-cast shadow on the surface
//   E4  Analytic aerial perspective (ground bluing near the limb)
//   E5  Terminator warmth on the ground (pink/orange sunrise-sunset band)
//   E6  Clouds occlude city lights from below; tighter night cutoff
//   E7  Cloud-top rim lighting at terminator

import { EARTH_CLOUD_COLUMN_GLSL } from './earthcloudcolumn.js';
import { EARTH_WEATHER_UV_GLSL } from './earthweather.js';
import { EARTH_CLOUD_TOP_GLSL } from './earthcloudtop.js';
import { OKLAB_MIX_GLSL } from './oklab.js';
import { ECLIPSE_GLSL } from '../eclipse/geometry.js';
import { EARTH_RINGS_GLSL } from '../earthrings.js';
import { EARTH_OCEAN_GLSL, EARTH_OCEAN_WAVES_GLSL } from './earthocean.js';
import { EARTH_IMAGERY_GLSL } from '../rocketry/earthimagery.js';
import { CLOUD_IMAGERY_GLSL } from '../rocketry/cloudimagery.js';
import { NIGHT_IMAGERY_GLSL } from '../rocketry/nightimagery.js';

export const earthVertexShader = `
  uniform sampler2D bumpTexture;
  uniform float displacementScale;

  varying vec2 vUv;
  varying vec3 vWorldNormal;
  varying vec3 vWorldPosition;
  varying vec3 vCameraVector;
  varying vec3 vLocalCameraVector;
  // The sun in the planet's own frame, for the cloud tops' tangent frame.
  uniform vec3 sunPosition;
  varying vec3 vLocalSun;
  // The sea's wave coordinates (earthocean.js EARTH_OCEAN_WAVES_GLSL): the
  // vertex from an anchor near the camera, a small float32 difference that
  // interpolates without losing the millimetres.
  uniform vec3 uWaveAnchor;
  varying vec3 vWaveRel;
  varying vec3 vWaveObjN;

  void main() {
    vUv = uv;
    vWaveRel = position - uWaveAnchor;
    vWaveObjN = position;
    // The elevation image encodes sea level as black and higher land as
    // positive heights. Subtracting 0.4 sank coasts and oceans by ~12 km,
    // separating the visible ground from the metre-scale cloud layer.
    float elevationBias = texture2D(bumpTexture, uv).r;
    vec3 displacedPosition = position + normalize(position) * elevationBias * displacementScale;

    vWorldNormal = normalize(mat3(modelMatrix) * normal);
    vWorldPosition = (modelMatrix * vec4(displacedPosition, 1.0)).xyz;
    vec4 viewPosition = modelViewMatrix * vec4(displacedPosition, 1.0);
    // The CPU-composed model-view matrix retains the small camera offset
    // at solar-system distances. Rotate this vector back to world axes
    // without reintroducing the large absolute translation.
    vCameraVector = -viewPosition.xyz * mat3(viewMatrix);
    vLocalCameraVector = -viewPosition.xyz * mat3(modelViewMatrix);
    vLocalSun = (sunPosition - vWorldPosition) * mat3(modelMatrix);
    gl_Position = projectionMatrix * viewPosition;
  }
`;

export const earthFragmentShader = `
  ${EARTH_IMAGERY_GLSL}
  uniform sampler2D dayTexture;
  uniform sampler2D nightTexture;
  uniform sampler2D cloudsTexture;
  uniform sampler2D normalTexture;
  uniform sampler2D oceanTexture;
  uniform float oceanTextureOn;
  uniform sampler2D bumpTexture;
  uniform vec2 dayTexelSize;
  uniform vec2 cloudTexelSize;
  uniform vec2 bumpTexelSize;
  uniform float surfaceDetailStrength;
  uniform float cloudDetailStrength;
  uniform vec3 sunPosition;
  uniform float cloudOpacity;
  uniform float uNightLights;  // 1, or 0 for an Earth with nobody on it (?whatif=future)
  uniform float weatherTime;
  uniform float time;
  uniform float uSimTime;
  uniform vec3 uSpinAxis;
  uniform int moonShadowCount;
  uniform vec3 moonShadowPositions[8];
  uniform float moonShadowRadii[8];
  // The Moon's shadow from the real geometry (src/eclipse/geometry.js), set
  // every frame by updateEarthEclipseUniforms in main.js: the Earth's and the
  // Moon's centres in world space, and the Moon and the Sun relative to the
  // Earth in kilometres along the world axes. uEclipseOn 0 keeps the older
  // drawn-radius spot below (materials built without these uniforms).
  uniform float uEclipseOn;
  // The skylight left under the Moon's shadow, a share of full sunlight.
  uniform float uEclipseSkyFloor;
  uniform vec3 uEclipseEarthCenter;
  uniform vec3 uEclipseMoonKm;
  uniform vec3 uEclipseSunKm;

  varying vec2 vUv;
  varying vec3 vWorldNormal;
  varying vec3 vWorldPosition;
  varying vec3 vCameraVector;
  varying vec3 vLocalCameraVector;
  varying vec3 vLocalSun;

  ${OKLAB_MIX_GLSL}
  ${ECLIPSE_GLSL}
  ${EARTH_RINGS_GLSL}
  ${EARTH_OCEAN_GLSL}
  ${EARTH_OCEAN_WAVES_GLSL}
  ${EARTH_CLOUD_COLUMN_GLSL}
  ${EARTH_WEATHER_UV_GLSL}
  ${EARTH_CLOUD_TOP_GLSL}
  ${CLOUD_IMAGERY_GLSL}
  ${NIGHT_IMAGERY_GLSL}

  // The city lights where there are none: what the night branch below
  // leaves when it is skipped.
  NightLights earthNoNightLights() {
    NightLights n;
    n.light = vec3(0.0);
    n.coarse = vec3(0.0);
    n.close = 0.0;
    n.page = 0.0;
    n.level = 0.0;
    n.display = 0.0;
    n.floor = 1.0;
    n.haze = 1.0;
    return n;
  }

  // Procedural detail synthesis, adds high-frequency surface and
  // cloud noise that fades in at close camera distances. The 4K
  // textures pixelate at ISS-altitude viewing; procedural noise
  // gives unlimited "zoom-in" detail without needing higher-res
  // assets. Same technique used for gas giants.
  float earthHash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
  }
  float earthValueNoise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(
      mix(earthHash(i), earthHash(i + vec2(1.0, 0.0)), u.x),
      mix(earthHash(i + vec2(0.0, 1.0)), earthHash(i + vec2(1.0, 1.0)), u.x),
      u.y
    );
  }
  float earthFbm(vec2 p) {
    float v = 0.0;
    float a = 0.5;
    for (int i = 0; i < 5; i++) {
      v += a * earthValueNoise(p);
      p *= 2.13;
      a *= 0.5;
    }
    return v;
  }

  void main() {
    vec3 baseNormal = normalize(vWorldNormal);
    vec3 V = normalize(vCameraVector);
    vec3 sunDir = normalize(sunPosition - vWorldPosition);

    // Shared twilight interval for terrain, city lights and cloud skylight.
    // Solar elevation drives the fade independently of viewing distance.
    float baseSunFacing = dot(baseNormal, sunDir);
    float dayFactor = smoothstep(-0.10, 0.22, baseSunFacing);
    // Cities emerge over the same twilight interval as the surface fades.
    // Separate overlapping ramps made bright city networks switch on ahead
    // of the surrounding terrain and cloud transition.
    float nightMask = 1.0 - dayFactor;
    // How much of the Sun the Moon hides from this point, 0 to 1.
    float eclipseHidden = 0.0;
    if (uEclipseOn > 0.5) {
      // From the normal, which is the sphere's radial direction to full
      // precision: at true scale the Earth is 5,000 units out, where a
      // world position steps by kilometres and the shadow's steep core
      // showed the steps as a grid of blocks.
      vec3 eclipseP = normalize(vWorldNormal) * ECL_R_EARTH;
      eclipseHidden = eclSunHidden(eclipseP, uEclipseSunKm, uEclipseMoonKm, ECL_R_MOON);
    }
    // The city lights are worked out only where they can show: where there
    // is night, past it to a sun 20 degrees up so every 2 x 2 block of
    // pixels the branch divides has none (its reads take their level from
    // their neighbours), anywhere in the Moon's shadow (totality lights them
    // by day, and its edge is far out in the penumbra, where they do not
    // show), and never on an Earth with nobody on it. The day side skips
    // their twenty-odd reads; a day view of the Earth spent a fifth of its
    // frame on them.
    bool nightHere = uNightLights > 0.0 && (baseSunFacing < 0.35 || eclipseHidden > 0.0);

    // === MULTI-OCTAVE BUMP + NORMAL-MAP RELIEF ===
    // Three-layer relief: fine grain (1× texel offset for ridge
    // lines and coastline edges), mid range (3× for sub-mountain
    // shape), coarse (8× for whole-mountain-range silhouette).
    // Each octave's slope is weighted into the final perturbed
    // normal so the surface reads with structure at every scale —
    // not just a uniform high-frequency wobble.
    //
    // East/north tangents derived from the sphere's geometric
    // normal (not dFdx of worldPos) so the silhouette stays
    // NaN-free.
    vec3 east  = normalize(vec3(baseNormal.z, 0.0, -baseNormal.x) + vec3(1e-5));
    vec3 north = normalize(cross(baseNormal, east));

    vec2 t1 = bumpTexelSize;
    vec2 t2 = bumpTexelSize * 3.0;
    vec2 t3 = bumpTexelSize * 8.0;
    // Octave 1 — finest grain.
    float bE1 = texture2D(bumpTexture, vUv + vec2(t1.x, 0.0)).r;
    float bW1 = texture2D(bumpTexture, vUv - vec2(t1.x, 0.0)).r;
    float bN1 = texture2D(bumpTexture, vUv + vec2(0.0, t1.y)).r;
    float bS1 = texture2D(bumpTexture, vUv - vec2(0.0, t1.y)).r;
    vec2 slope1 = vec2(bE1 - bW1, bN1 - bS1);
    // Octave 2 — sub-mountain shape.
    float bE2 = texture2D(bumpTexture, vUv + vec2(t2.x, 0.0)).r;
    float bW2 = texture2D(bumpTexture, vUv - vec2(t2.x, 0.0)).r;
    float bN2 = texture2D(bumpTexture, vUv + vec2(0.0, t2.y)).r;
    float bS2 = texture2D(bumpTexture, vUv - vec2(0.0, t2.y)).r;
    vec2 slope2 = vec2(bE2 - bW2, bN2 - bS2);
    // Octave 3 — full mountain range silhouette.
    float bE3 = texture2D(bumpTexture, vUv + vec2(t3.x, 0.0)).r;
    float bW3 = texture2D(bumpTexture, vUv - vec2(t3.x, 0.0)).r;
    float bN3 = texture2D(bumpTexture, vUv + vec2(0.0, t3.y)).r;
    float bS3 = texture2D(bumpTexture, vUv - vec2(0.0, t3.y)).r;
    vec2 slope3 = vec2(bE3 - bW3, bN3 - bS3);

    // Balanced octave weights — coarse pulled back from 1.1 to 0.55
    // because at the previous setting the Andes and Himalayas read
    // as nearly-black bands across their full latitudes. The new
    // mid-heavy weighting still shows continent-scale relief but
    // the coarse octave no longer drowns the texture's actual
    // surface albedo on steep mountain ranges.
    // Reference-grounded relief weighting. Real orbital photography
    // (Apollo Blue Marble, ISS window shots) shows almost NO embossed
    // mountain shading — land reads as flat albedo softened by
    // atmosphere, and you genuinely cannot tell the Himalayas are 3D
    // from orbit. The earlier mid/coarse-heavy weighting turned the
    // Tibetan Plateau and the Andes into a crisp topographic relief
    // map, which is the single biggest tell that breaks the photo
    // illusion. Coarse (whole-range silhouette) and mid (sub-mountain
    // shape) are the offenders, so they're cut hardest; fine grain is
    // kept higher because it only reads at close zoom where some
    // ridge structure is actually wanted.
    vec2 bumpSlope = slope1 * 0.55 + slope2 * 0.6 + slope3 * 0.32;
    // Amplitude cut from 10 → 5.5. Combined with the lighter octave
    // weights this drops the orbital embossing ~60-65% so the disc
    // reads as a softened photo rather than an embossed globe, while
    // still leaving enough relief that a close fly-down isn't dead
    // flat.
    float bumpScale = 5.5;

    // Normal-map sample for high-frequency detail (rivers, cracks,
    // ridge crests). Stored in RGB; decode to bipolar slope.
    vec3 normalMapSample = texture2D(normalTexture, vUv).xyz * 2.0 - 1.0;
    // Normal map is in tangent space — x is east, y is north, z is
    // up. We only need x/y for the slope contribution.
    vec2 normalMapSlope = normalMapSample.xy * 0.35;

    vec3 perturbedNormal = normalize(
      baseNormal
      - east  * (bumpSlope.x * bumpScale + normalMapSlope.x)
      - north * (bumpSlope.y * bumpScale + normalMapSlope.y)
    );
    // Use perturbed normal for diffuse lighting only on the lit
    // hemisphere — past the terminator we want the city-lights
    // texture to do the heavy lifting unmodulated.
    float perturbedSunFacing = dot(perturbedNormal, sunDir);
    float sunFacing = mix(baseSunFacing, perturbedSunFacing, smoothstep(-0.1, 0.15, baseSunFacing));
    float diffuse = max(sunFacing, 0.0);

    // Intersect the view ray with the same 2 km cloud shell used by the
    // orbital column. The old fixed UV offset moved distant clouds by
    // hundreds of kilometres, producing doubled fronts during the handoff.
    float cloudLon = (vUv.x - 0.5) * 6.28318530718;
    float cloudLat = (vUv.y - 0.5) * 3.14159265359;
    vec3 localN = vec3(cos(cloudLat)*cos(cloudLon), sin(cloudLat), -cos(cloudLat)*sin(cloudLon));
    vec3 localV = normalize(vLocalCameraVector);
    float shellDelta = (2000.0/6371000.0) * (2.0 + 2000.0/6371000.0);
    float shellMu = max(dot(localN,localV),0.0);
    float shellT = shellDelta / (sqrt(shellMu*shellMu+shellDelta)+shellMu);
    // Read the full-resolution cloud reference at the evolving weather
    // field's address, so the sharp globe clouds sit exactly where the
    // in-atmosphere volumes (fed by that field) draw theirs.
    vec3 cloudN = earthWeatherWarp(normalize(localN + localV*shellT), weatherTime);
    float cloudNLat = asin(clamp(cloudN.y,-1.0,1.0));
    vec2 cloudUV = vec2(atan(-cloudN.z,cloudN.x)/6.28318530718+0.5-earthWeatherDrift(cloudNLat, weatherTime),
        cloudNLat/3.14159265359+0.5);
    // Keep derivatives continuous at +/-180 degrees. RepeatWrapping owns
    // the address wrap; an atan jump here would select the coarsest mip.
    cloudUV.x += floor(vUv.x - cloudUV.x + 0.5);

    vec3 lumaWeights = vec3(0.2126, 0.7152, 0.0722);
    vec4 dayColor = texture2D(dayTexture, vUv);
    vec3 dayRaw = dayColor.rgb;
    // The ocean texture at this point, read once for the night's sea and the
    // water below.
    vec3 oceanHere = texture2D(oceanTexture, vUv).rgb;
    vec4 nightColor = vec4(0.0);
    float cityRegion = 0.0;
    vec2 nightSea = vec2(0.0);
    NightLights nightL = earthNoNightLights();
    if (nightHere) {
      nightColor = texture2D(nightTexture, vUv);
      // The city lights (rocketry/nightimagery.js): the 8K as always while a
      // texel is under a pixel, then a B-spline read of it, the 500 m Black
      // Marble pages over it and the structure under them as the view closes
      // in. The region's share of LED white is the same slow noise that tints
      // the cities below.
      cityRegion = earthValueNoise(vUv * 14.0 + vec2(7.3, 11.1));
      // The sea for the lights (the ocean texture's coverage, the coast the
      // water below is drawn to; the day map's flat sea until it loads): no
      // streets or lamps on it, and close in no shore spill either.
      float nightSeaCover = oceanTextureOn > 0.5 ? oceanHere.r : earthOceanFlat(dayRaw);
      float nightSeaOpen = oceanTextureOn > 0.5 ? textureLod(oceanTexture, vUv, 2.5).r : 0.0;
      nightSea = nightWater(nightSeaCover, nightSeaOpen);
      // Under a rocketry terrain patch the patch draws the lights (its lights
      // pass), so the close-in work is skipped there.
      nightL = earthNightLights(nightTexture, vUv, 1.0 / vec2(textureSize(nightTexture, 0)), smoothstep(0.40, 0.70, cityRegion), nightUnderPatch(vUv), nightSea);
    }
    // The map with its streamed 1 km pages over it where they are resident
    // (rocketry/cloudimagery.js).
    CloudMapRead cloudRead = cloudMapRead(cloudsTexture, cloudUV, cloudTexelSize, cloudDetailStrength);
    vec4 clouds = vec4(cloudRead.m);
    // The sea from earth_ocean.webp (earthocean.js): coverage, depth, Kd490.
    // Until it has loaded, the day map's flat sea colour stands in for the
    // coverage, as deep clear water.
    float seaFlat = earthOceanFlat(dayRaw);
    EarthOcean sea = earthOceanDecode(oceanTextureOn > 0.5
      ? oceanHere
      : vec3(seaFlat, seaFlat * 0.8913, seaFlat * 0.1717));
    // The coverage sharpened to its own coastline where a texel spans many
    // pixels, and the plain filtered coverage once a pixel spans texels.
    float seaEdge = clamp(fwidth(sea.cover), 0.02, 0.5);
    float oceanMask = smoothstep(0.5 - seaEdge, 0.5 + seaEdge, sea.cover);

    // Cloud tops (earthcloudtop.js), at every distance: under the map's
    // texel the carve fills in the cells the data cannot resolve, and the
    // pixel's footprint only filters them, so from far off this is the map's
    // own mean with nothing else to hand over to. rocketry's orbitclouds.js
    // runs the same calls term for term, which is what keeps the patch rim
    // invisible. The footprint is the pixel's true size on the ground, the
    // same along both screen axes (the geometric mean of the two), so a drag
    // or a tilt blurs the cells without moving them.
    float ctPix = sqrt(length(dFdx(localN)) * length(dFdy(localN))) * CT_RADIUS;
    float ctLat = (cloudUV.y - 0.5) * 3.14159265359;
    float ctLon = (cloudUV.x - 0.5) * 6.28318530718;
    vec3 ctNd = vec3(cos(ctLat) * cos(ctLon), sin(ctLat), -cos(ctLat) * sin(ctLon));
    float ctMapM = cloudTexelSize.y * 3.14159265359 * CT_RADIUS;
    CloudMapPoint ctMap = cloudPagedMapPoint(cloudsTexture, cloudUV, cloudTexelSize, cloudRead, ctLat, cloudOpacity);
    CloudTop ct = cloudTopField(ctNd, ctMap, ctPix, uCloudTopQuality > 0.5 ? 3 : 2);
    // The map's own level for this footprint, for reads inside branches.
    float ctMapLod = max(log2(ctPix / ctMapM), 0.0);
    // The sun in the planet's own frame, then in the tangent frame at this
    // point; the drifted address is a rotation about the spin axis away,
    // so the same components hold there.
    vec3 sunLocal = normalize(vLocalSun);
    vec3 eastL = normalize(vec3(localN.z, 0.0, -localN.x) + vec3(1e-5));
    vec3 northL = cross(localN, eastL);
    vec3 ctSun = vec3(dot(sunLocal, eastL), dot(sunLocal, northL), dot(sunLocal, localN));
    // The domes sunward shading this one, where the relief is resolved
    // (orbitclouds.js makes the same call).
    ct.occ = cloudTopSunOcclusion(ctNd, ctMap, ct, ctPix, ctSun);
    float ctGround = 0.0;
    if (ctSun.z > 0.02) {
      ctGround = cloudPagedGroundShadow(cloudsTexture, cloudTopGroundAddress(ctNd, normalize(localN + localV * shellT), localN), ctSun, ctPix, ctMap, cloudOpacity, ctMapM);
    }
    // The rest of this shader reads the cover in the map's own units (the
    // ocean's tint, the glint, the city lights under cloud): the carve's
    // opacity scaled back to the map, so its mean is the map's value.
    clouds.r = clamp(ctMap.m * ct.alpha / max(ctMap.alpha, 1e-4), 0.0, 1.0);

    // Luma-only unsharp-mask clarity pass (sharpens detail without
    // pushing colour saturation, preserving the photographic
    // character). The blur is a 4-tap cross around the fragment; the
    // high-frequency residual (texture - blur) is added back to crisp
    // up coastlines, terrain and texture edges.
    //
    // Strength RAMPS UP as the camera approaches: at orbital distance
    // it's a firm overall tighten of the 8K daymap; on a close
    // fly-down it becomes a strong sharpen so the surface stays crisp
    // instead of going soft as texels grow on screen. Distance is
    // computed inline here (the canonical earthCamDist is derived a
    // few lines below for the procedural-detail fade).
    vec3 dayBlur = (
      texture2D(dayTexture, vUv + vec2(dayTexelSize.x, 0.0)).rgb +
      texture2D(dayTexture, vUv - vec2(dayTexelSize.x, 0.0)).rgb +
      texture2D(dayTexture, vUv + vec2(0.0, dayTexelSize.y)).rgb +
      texture2D(dayTexture, vUv - vec2(0.0, dayTexelSize.y)).rgb
    ) * 0.25;
    float sharpenDist = length(vCameraVector);
    float sharpenClose = 1.0 - smoothstep(2.5 * 1.25, 2.5 * 5.0, sharpenDist);
    // Far orbit ≈ 1.2× surfaceDetailStrength; close ≈ 3.0× for a firm
    // edge bite. Clamp widened to ±0.14 so high-contrast edges
    // (coastlines, snow lines) can sharpen harder up close.
    float sharpenStrength = surfaceDetailStrength * (1.2 + sharpenClose * 1.8);
    float dayDetail = clamp(dot(dayColor.rgb - dayBlur, lumaWeights), -0.14, 0.14);
    dayColor.rgb = clamp(dayColor.rgb + vec3(dayDetail * sharpenStrength), 0.0, 1.0);

    // === PROCEDURAL CLOSE-UP DETAIL SYNTHESIS ===
    // Camera-distance fade. ISS altitude is ~0.16 scene units above
    // Earth's surface (Earth radius 2.5, ISS at 0.064 above that);
    // at "Earth radius * 1.5" we go to full procedural detail, fading
    // to none by "Earth radius * 6". Earth radius is hardcoded as 2.5
    // here because it's the artistic scale; realistic-mode users
    // wouldn't be inside this distance anyway.
    float earthCamDist = length(vCameraVector);
    float earthDetailFade = 1.0 - smoothstep(2.5 * 1.5, 2.5 * 6.0, earthCamDist);
    // Extreme close-up factor — ramps 0→1 only as the camera drops
    // from ~1.7R down toward the surface (max zoom). At that range a
    // single base texel covers many screen pixels and bilinear
    // filtering smears the 8K daymap to mush; this drives the
    // highest-frequency synthetic detail that masks the blur so the
    // surface reads crisp instead of soft. Costs nothing at orbital
    // distance because it's zero there.
    float earthMacroClose = 1.0 - smoothstep(2.5 * 1.04, 2.5 * 1.7, earthCamDist);

    // The ocean's share of the close-up detail, applied to the water's own
    // colour once it is known (below).
    float seaDetail = 1.0;
    if (earthDetailFade > 0.001) {
      // Land: high-frequency terrain micro-texture (rocks, forest
      // canopy patterns, desert dunes). UV scale 200× the base
      // texture so each procedural feature is smaller than a texel.
      vec2 surfDetailUv = vUv * 200.0;
      float surfFine = earthFbm(surfDetailUv);
      float surfMid  = earthFbm(surfDetailUv * 0.4 + vec2(11.3, 7.7));
      float surfDetail = (surfFine * 0.65 + surfMid * 0.35 - 0.5) * 2.0;
      // Ultra-fine terrain octaves — two stacked high-frequency FBM
      // layers (900× and 1900×) that only swell in under earthMacroClose.
      // Multi-scale so it reads as fractal terrain roughness (the way
      // real ground gains detail as you descend) rather than uniform
      // static. This is the layer doing the heavy lifting on the
      // maximally-zoomed view.
      float surfUltra = (earthFbm(vUv * 900.0) * 0.62 + earthFbm(vUv * 1900.0 + vec2(5.1, 2.7)) * 0.38 - 0.5) * 2.0;
      // Ocean: subtler ripple-scale modulation, larger UV scale so
      // the noise reads as "wave structure" rather than "land
      // texture".
      vec2 oceanDetailUv = vUv * 80.0;
      float oceanRipples = earthFbm(oceanDetailUv) - 0.5;
      // Fine wave sparkle at extreme zoom. Doubles as a band-breaker:
      // its high-frequency luminance jitter dithers away the smooth
      // concentric specular-falloff rings that otherwise show as faint
      // ripples in the close-up sub-solar ocean.
      float oceanFine = (earthFbm(vUv * 680.0) * 0.6 + earthFbm(vUv * 1500.0 + vec2(8.3, 1.9)) * 0.4 - 0.5) * 2.0;
      // Mix detail types based on the ocean mask. Base micro-texture
      // plus the ultra-fine octaves swelling in at max zoom so the
      // synthetic detail dominates the blurred texel underneath
      // (land amplitude climbs 0.10 → ~0.23, ocean 0.04 → ~0.10).
      float landDetail  = surfDetail  * (1.0 - oceanMask) * 0.10
                        + surfUltra   * (1.0 - oceanMask) * 0.13 * earthMacroClose;
      float oceanDetail = oceanRipples * 0.04
                        + oceanFine    * 0.06 * earthMacroClose;
      dayColor.rgb *= 1.0 + landDetail * earthDetailFade;
      seaDetail = 1.0 + oceanDetail * earthDetailFade;

    }

    // Apply photographic pages after synthetic fallback detail, so the
    // old blur-hiding noise does not muddy real roads and terrain. Missing
    // pages retain the detailed globe through the same gradual crossfade.
    // This read keeps them to the ocean texture's land, as from afar they
    // always were.
    vec3 imgMasked = earthImagery(vUv, dayColor.rgb);
    vec2 imgPage = earthImageryPage(vUv);
    vec2 imgDx = dFdx(imgPage), imgDy = dFdy(imgPage);
    // Up close the pages draw the coast. The ocean texture's texels are 5 km,
    // so where one spans many pixels its coverage, sharpened to the 0.5
    // contour, put a polygonal shore kilometres off the real one: a band of
    // the pages' own sea lit as land inside it, the shallow-water tint of the
    // column outside it. Where the pages' texels are a few times finer than
    // the texture's and the pixel is finer than its texel, the pages are read
    // over land and water alike and their own colour says what is water
    // (earthImageryWater, with the texture's coverage as a prior), so the
    // sea's colour, glint and waves stop at the shore in the photograph.
    // From afar (a texel under two and a half pixels) the texture's polygons
    // do not show, nothing here runs, and the globe is as it was: from 2
    // Earth radii out a pixel at the Max preset spans 2.5 km and more.
    // Only near a coast the texture knows of, about 20 km: out at sea and
    // far inland the texture stays the judge, so a sediment plume keeps the
    // sea's glint and a shaded snowfield stays land.
    vec4 img = vec4(dayColor.rgb, 0.0);
    float imgFine = 0.0;
    if (ctPix < 0.4 * EO_MASK_TEXEL_M) {
      float imgCells;
      img = earthImageryOpen(vUv, dayColor.rgb, imgDx, imgDy, imgCells);
      // Past the limb the read can come back NaN.
      if (any(notEqual(img, img))) img = vec4(dayColor.rgb, 0.0);
      float imgTexelM = imgCells / max(img.a, 1e-4) * EARTH_IMAGERY_TEXEL_DEG * 111195.0;
      imgFine = img.a * (1.0 - smoothstep(0.15, 0.4, imgTexelM / EO_MASK_TEXEL_M))
              * (1.0 - smoothstep(0.17, 0.4, ctPix / EO_MASK_TEXEL_M));
      float coastWide = oceanTextureOn > 0.5 ? textureLod(oceanTexture, vUv, 2.0).r : 0.5;
      float coastNear = smoothstep(0.005, 0.04, coastWide) * (1.0 - smoothstep(0.96, 0.995, coastWide));
      if (imgFine * coastNear > 0.001) {
        // Read for this over at least three of the pages' texels, so a
        // speckle of JPEG colour in the water is not a rock and the shore is
        // feathered over a few texels instead of stepping along them.
        float widen = max(1.0, 3.0 * imgTexelM / max(ctPix, 1e-3));
        float wideCells;
        vec3 imgWide = earthImageryOpen(vUv, dayColor.rgb, imgDx * widen, imgDy * widen, wideCells).rgb;
        oceanMask = mix(oceanMask, earthImageryWater(imgWide, sea.cover), imgFine * coastNear);
      }
    }
    dayColor.rgb = imgFine > 0.0 ? mix(imgMasked, img.rgb, imgFine) : imgMasked;
    // The ground's albedo (earthocean.js): earthLandAlbedo from afar, where
    // it was fitted to DSCOVR EPIC, and the brightness-preserving form up
    // close, where single roofs and salt flats are resolved (it takes over
    // as the pixel shrinks from 2 to 0.8 km, with the coast above, so no
    // globe view changes).
    float closeAlbedo = 1.0 - smoothstep(800.0, 2000.0, ctPix);

    // === OCEAN ===
    // The sea as reflectance (earthocean.js): the water column over the
    // bottom by depth and Kd490 where the day map shows its flat sea colour,
    // and the map's own painted water (turquoise banks, sediment plumes)
    // carried into the same reflectance where it has one, the two blended
    // across the flat ramp. EARTH_OCEAN_ALBEDO turns reflectance into this
    // shader's albedo, which the lighting below scales by EARTH_LIGHT.
    float seaMuS = clamp(baseSunFacing, 0.05, 1.0);
    float seaMuV = clamp(dot(baseNormal, V), 0.05, 1.0);
    float seaLatDeg = abs(vUv.y - 0.5) * 180.0;
    // The wind over the sea (earthocean.js), and up close the waves it
    // raises (EARTH_OCEAN_WAVES_GLSL): a tilted facet normal, the slope
    // variance they leave to the glint's lobe, how far they are resolved and
    // their whitecaps. The coordinates' screen derivatives are taken here,
    // outside any branch. From orbit this is the plain sphere and Cox-Munk.
    float seaWind = earthOceanWind(vUv, ctPix);
    vec3 waveP = vWaveRel * uWaveScale + uWavePhase;
    vec3 waveDx = dFdx(waveP);
    vec3 waveDy = dFdy(waveP);
    SeaWaves waves = SeaWaves(baseNormal, earthSlopeVariance(seaWind), 0.0, 0.0, vec2(0.0), 1e6);
    if (uWavesOn > 0.5 && oceanMask > 0.001) waves = earthSeaWaves(waveP, waveDx, waveDy, baseNormal, seaWind);
    // The old blur-hiding noise on the sea gives way to the waves.
    seaDetail = mix(seaDetail, 1.0, waves.resolved);
    vec3 seaColumn = earthWaterReflectance(sea.depth, sea.kd490, seaLatDeg, seaMuS, seaMuV);
    vec3 seaPainted = earthPaintedWater(imgMasked, seaMuV);
    // Within a texel or two of the shore the day map's filtered colour is part
    // land, which would read as painted water and draw a dark line round every
    // coast; the column model takes those. The coverage a level and a half up
    // from this pixel's own mip is below 1 there.
    vec2 seaTexels = vUv * vec2(8192.0, 4096.0);
    float seaLod = max(log2(max(length(dFdx(seaTexels)), length(dFdy(seaTexels)))), 0.0);
    float seaOpen = smoothstep(0.9, 1.0, textureLod(oceanTexture, vUv, seaLod + 1.5).r) * oceanTextureOn;
    vec3 seaAlbedo = mix(seaColumn, seaPainted, (1.0 - seaFlat) * seaOpen) * seaDetail;
    // Up close the water is the pages' own: the banks, channels, reefs and
    // plumes the photograph shows, taken through the same albedo as the land
    // beside it so the picture keeps its land-to-sea contrast, with the sky
    // light off its surface (the column's EO_SKY term) added back. Only once
    // a pixel is under 40 to 120 m: from further up the 1999 composite's
    // water shows its scenes' straight edges and speckle, and the column
    // (5 km depth and Kd490) draws the open sea more cleanly. Water the
    // pages found where the texture has none (the real shore inside its
    // coarse land) has no depth to draw a column with, and takes the pages'
    // colour from any height.
    float imgSea = imgFine * max(1.0 - smoothstep(40.0, 120.0, ctPix), 1.0 - smoothstep(0.3, 0.8, sea.cover));
    if (imgSea > 0.0) {
      vec3 imgAlbedo = earthLandAlbedo(img.rgb);
      if (closeAlbedo > 0.0) imgAlbedo = mix(imgAlbedo, earthLandAlbedoClose(img.rgb), closeAlbedo);
      seaAlbedo = mix(seaAlbedo, imgAlbedo + earthSkyGlare(seaMuV), imgSea);
    }
    seaAlbedo *= EARTH_OCEAN_ALBEDO;
    // Where the waves are resolved their facets mirror the sky themselves
    // (added after the light, below), in place of the nadir-fitted term.
    seaAlbedo = max(seaAlbedo - earthSkyGlare(seaMuV) * waves.resolved * EARTH_OCEAN_ALBEDO, vec3(0.0));
    // Whitecaps: foam is a bright diffuse surface over the water.
    seaAlbedo = mix(seaAlbedo, vec3(0.60, 0.62, 0.64), waves.foam);
    dayColor.rgb = mix(dayColor.rgb, seaAlbedo, oceanMask);

    // Land albedo (earthocean.js earthLandAlbedo): the day map's bright soils
    // brought down to their reflectance, calibrated against DSCOVR EPIC.
    const float LAND_GREEN_DESAT = 0.6;
    vec3 landAlbedo = earthLandAlbedo(dayColor.rgb);
    if (closeAlbedo > 0.0) landAlbedo = mix(landAlbedo, earthLandAlbedoClose(dayColor.rgb), closeAlbedo);
    dayColor.rgb = mix(landAlbedo, dayColor.rgb, oceanMask);

    // Tame over-vibrant vegetation. Real orbital photography shows
    // land greens as muted olive/khaki, not the vivid emerald the raw
    // satellite daymap reads as once lit and bloomed. Pull saturation
    // out of GREEN-DOMINANT land pixels only (where green is the max
    // channel), leaving deserts, ocean, snow and ice untouched. The
    // greenness is relative to the pixel's own green: measured on the
    // absolute difference, dark rainforest (0.04, 0.06, 0.01) barely moved,
    // where EPIC shows the Amazon grey-olive.
    {
      float greenDom = step(dayColor.r, dayColor.g) * step(dayColor.b, dayColor.g);
      float greenness = clamp((dayColor.g - max(dayColor.r, dayColor.b)) / max(dayColor.g, 1e-3) * 2.0, 0.0, 1.0);
      float greenDesat = greenness * greenDom * (1.0 - oceanMask) * LAND_GREEN_DESAT;
      float greenLuma = dot(dayColor.rgb, lumaWeights);
      dayColor.rgb = mix(dayColor.rgb, vec3(greenLuma), greenDesat);
    }

    // Break the "texture-wrapped" look on land. A single satellite
    // daymap stretched over a sphere reads as a uniform decal; real
    // terrain has large-scale regional tonal variation (albedo, dust,
    // vegetation density, haze pooling). Two very-low-frequency noise
    // fields gently modulate land brightness AND a touch of warm/cool
    // tone in big lazy patches, so the surface stops looking pasted on.
    // Land only (ocean has its own bathymetry + glint treatment).
    {
      float macroA = earthFbm(vUv * 3.4 + vec2(2.7, 8.1));
      float macroB = earthFbm(vUv * 7.3 + vec2(11.3, 4.9));
      float macro = (macroA * 0.62 + macroB * 0.38 - 0.5) * 2.0; // ~[-1,1]
      float landMask = 1.0 - oceanMask;
      // Brightness patches (+/-7%) plus a faint warm/cool tone shift so
      // it doesn't read as flat dimming.
      dayColor.rgb *= 1.0 + macro * 0.07 * landMask;
      dayColor.r   *= 1.0 + macro * 0.018 * landMask;
      dayColor.b   *= 1.0 - macro * 0.018 * landMask;
    }

    // === Day-side base ===
    // Lambert with a modest ambient floor (0.25) so the surface
    // brightness ramps down naturally toward the terminator without
    // diving to near-black before dayFactor takes over. The earlier
    // (0.10 + 0.90 * diffuse) was too sharp, surface went near-black
    // at terminator with no glow contribution, producing a hard cut.
    // The 0.25 floor is the right MAGNITUDE (0.10 was tried and cut too
    // hard at the terminator) but it was neutral grey, and a colourless
    // constant ambient is the most reliable "this is CG" tell there is.
    // Ground not facing the sun is lit by the whole sky dome, so it
    // should read Rayleigh blue while the sun is high and go warm as the
    // sun drops, because by then the column overhead has scattered its
    // blue out and only long wavelengths are left to bounce down.
    //
    // The tint is normalised to unit luminance before use, so this
    // changes HUE ONLY: total ambient energy is unchanged and the
    // day-to-night ramp tuned above behaves exactly as before. Setting
    // SKY_AMBIENT_CHROMA to 0.0 reproduces the old flat grey precisely.
    const float SKY_AMBIENT_CHROMA = 1.0;
    // earthocean.js: amber under a low sun, deep Rayleigh blue under a high
    // one, at unit luminance.
    vec3 skyTint = mix(vec3(1.0), earthSkyTint(baseSunFacing), SKY_AMBIENT_CHROMA);
    // The sea is level, so its light comes off the sphere's own normal: the
    // relief map's slopes at a coast would shade the water beside it.
    float litDiffuse = mix(diffuse, max(baseSunFacing, 0.0), oceanMask);
    // Up close, where the ground is the photograph's own colour
    // (earthLandAlbedoClose), its light is the daylight that colour is
    // relative to: sun and sky together white on level ground, the direct
    // beam as much warmer than white as the sky is bluer (the blue it lost
    // on the way down is the sky's). With a white sun the blue sky made the
    // whole of the light blue, a quarter more blue than red at noon, and the
    // ground took a violet cast. From afar the lighting the EPIC fit was made
    // under is kept.
    vec3 sunTint = vec3(1.0);
    if (closeAlbedo > 0.0) {
      float levelSun = max(baseSunFacing, 0.0);
      vec3 balanced = max((0.25 + 0.75 * levelSun - 0.25 * skyTint) / max(0.75 * levelSun, 1e-3), vec3(0.0));
      sunTint = mix(vec3(1.0), balanced, closeAlbedo * smoothstep(0.1, 0.3, levelSun));
    }
    vec3 dayLit = dayColor.rgb * (skyTint * 0.25 + litDiffuse * 0.75 * sunTint) * EARTH_LIGHT;

    // Bump-derived horizon-shadow shading. Sample the elevation map
    // at the surface point and again at a small offset along the
    // sun-tangential direction; if the sun-side neighbour is HIGHER
    // (mountain or rim ahead of us blocking the sun), darken the
    // surface here. Cheap mountain-shadow approximation that doesn't
    // need TBN math (which had NaN flicker problems on the silhouette
    // when we tried real normal-map perturbation). Gated by diffuse
    // so the effect only applies to lit pixels.
    // Horizon shadow — multi-step ray-march along the sun-tangent
    // direction. Sample the bump map at 3 progressively further
    // offsets; if any of them is significantly higher than the
    // current fragment, this pixel is in the shadow of a mountain
    // ahead. Combined with the bump-based normal perturbation
    // above, you get both the lit-vs-shadowed face of every
    // mountain AND the dramatic long shadows cast across valleys
    // at low sun angles.
    float bumpHere = texture2D(bumpTexture, vUv).r;
    vec2 baseSunStep = vec2(dot(sunDir, east), dot(sunDir, north)) * bumpTexelSize;
    float horizonShadow = 0.0;
    {
      // Three steps but tighter range than before — the long 18×
      // sample was reaching ~80 km, far enough that the Andes were
      // casting shadows clear across the Amazon basin. Pulled the
      // longest sample back to 10× and raised the obstruction-
      // threshold offsets so only really tall obstructions cast
      // distant shadows. Near-field shadow at 3× / 6× still gives
      // mountain ranges their proper ridge-shadow look.
      float bA1 = texture2D(bumpTexture, vUv + baseSunStep * 3.0).r;
      float bA2 = texture2D(bumpTexture, vUv + baseSunStep * 6.0).r;
      float bA3 = texture2D(bumpTexture, vUv + baseSunStep * 10.0).r;
      horizonShadow = max(max(bA1 - bumpHere, bA2 - bumpHere - 0.10), bA3 - bumpHere - 0.22);
      horizonShadow = max(horizonShadow, 0.0);
    }
    // Strong darkening at the terminator, gentler at noon. Pulled
    // back further (0.85 max from 1.1) because the cast mountain
    // shadows were adding to the over-embossed relief look — real
    // orbital photos away from the terminator show barely any
    // mountain-cast shadow. Terminator-side shadowing stays since
    // grazing sun genuinely does throw long shadows there, but the
    // mid-disc contribution is now low.
    float horizonShadowStrength = mix(0.28, 0.85, 1.0 - smoothstep(0.0, 0.3, baseSunFacing));
    dayLit *= 1.0 - horizonShadow * horizonShadowStrength * diffuse;
    // Higher-elevation pixels get a small brightness lift — mimics
    // snow-pack / atmospheric brightening on real ranges. Halved
    // (0.16 → 0.08) because it was making the Himalayas and Tibet
    // glow as a bright white band, which combined with the relief
    // shading read as a fake topographic highlight rather than the
    // soft snow the real photos show.
    dayLit *= 0.98 + bumpHere * 0.08;

    // Cloud shadow on the surface: the cloud tops' own probe along the sun
    // (earthcloudtop.js), per channel: plain on land, on the sea the light
    // from the water column and not the sky its surface reflects. The air
    // light over it is shaded apart, on the veil below. From far off it is
    // the map's opacity where the sun's line meets the cloud, so the shadow
    // falls beside the cloud only where the cloud is resolved.
    float ctShadowSun = ctGround * smoothstep(0.0, 0.3, baseSunFacing);
    dayLit *= 1.0 - ctShadowSun * cloudTopShadowStrength(oceanMask);

    // Sun glint off the sea (earthocean.js): Cox-Munk facets under a wind
    // that varies from place to place, mirrored between the sun and this
    // view, so the spot sits where the sea tilts the sun into the camera and
    // moves with the view as a real one does. Its brightness is the slopes'
    // own statistics at every range: at a full disc it is the broad silver
    // patch DSCOVR shows, and from low orbit a softer sheen across the view.
    // It is sunlight, so it takes the lit albedo's direct share (0.75 of
    // EARTH_LIGHT) and the cloud's shadow in full; the sun's colour through
    // the air comes from the air-mass term below.
    float glint = earthGlintS2(waves.n, sunDir, V, waves.s2) * (1.0 - waves.foam);
    // Up close the glint breaks into glitter (EARTH_OCEAN_WAVES_GLSL).
    if (waves.resolved > 0.001 && glint > 1e-4) {
      glint *= mix(1.0, earthGlitter(waves, earthGlintDensity(waves.n, sunDir, V, waves.s2), time), waves.resolved);
    }
    dayLit += earthGlintTint(seaMuV) * (glint * EARTH_OCEAN_ALBEDO * 0.75 * EARTH_LIGHT * oceanMask * (1.0 - ctGround));
    // Up close each facet mirrors its own piece of sky with its own Fresnel:
    // faces turned from the camera catch the bright horizon, faces toward it
    // the deep blue overhead, which is what draws the waves away from the
    // glint.
    if (waves.resolved > 0.001) {
      vec3 skyRay = reflect(-V, waves.n);
      float facing = 1.0 - clamp(dot(waves.n, V), 0.0, 1.0);
      float fresnel = 0.0204 + 0.9796 * facing * facing * facing * facing * facing;
      dayLit += earthReflectedSky(dot(skyRay, baseNormal), baseSunFacing) * fresnel * waves.resolved * oceanMask * (1.0 - waves.foam);
    }

    // (Whitecaps removed — at the camera distances this scene
    // typically uses they read as random sparkle particles rather
    // than convincing foam, even with the noise threshold tuned
    // tight. The streaky glint above + the ocean texture do the
    // "this is water" work cleanly enough without the speckle.)

    // Snow / ice / cloud-top specular. High-albedo surfaces (polar
    // caps, snow-covered mountain ranges, ice fields) catch direct
    // sunlight as a soft Phong-style highlight using the bump-
    // perturbed normal — this is what makes alpine ranges in ISS
    // photos read with bright sunlit western slopes vs. shadowed
    // eastern slopes. View-dependent: the highlight tracks the
    // camera through the half-vector calculation. Land-only via
    // (1 - oceanMask) so it doesn't double-up with the ocean
    // glint above.
    vec3 H = normalize(sunDir + V);
    float landSpec = pow(max(dot(perturbedNormal, H), 0.0), 28.0);
    float brightnessAlbedo = smoothstep(0.55, 0.85, dot(dayColor.rgb, lumaWeights));
    float landSpecMask = (1.0 - oceanMask) * brightnessAlbedo * diffuse;
    // Pulled back (0.45 → 0.30) so sunlit snow/ice slopes don't
    // read as blown-out white crests on top of the now-softer
    // relief. Still enough to give alpine ranges a sunlit sheen at
    // close zoom without the orbital "glowing mountains" tell.
    dayLit += vec3(1.0, 0.97, 0.92) * landSpec * landSpecMask * 0.30;

    // Physical sunset/sunrise coloring via atmospheric air-mass
    // attenuation (Heckel 2026, Hillaire 2020 approach). Instead of
    // a hand-tuned smoothstep multiplier at the terminator band,
    // compute the actual path length the sun's light traveled
    // through Earth's atmosphere to reach this surface point. The
    // Kasten-Young air-mass formula 1/(cos(zenith) + 0.16) handles
    // the grazing-horizon case smoothly without blowing up: it
    // returns 1.0 at zenith (sun overhead, sea-level vertical
    // thickness) and roughly 38 at the horizon, matching the real
    // optical air mass curve. Per-wavelength Beer-Lambert extinction
    // on (1.0, 1.0, 1.0) white sunlight then naturally produces the
    // golden-hour amber and deep-red sunset colors as the blue/green
    // wavelengths are stripped out of the surviving spectrum. The
    // resulting gradient is continuous (no smoothstep edges) and
    // physically anchored, so dawn/dusk transitions read smoother
    // and the saturation peak at horizon is automatically correct.
    // (earthocean.js earthSunThroughAir, shared with rocketry's far sea.)
    dayLit *= earthSunThroughAir(baseSunFacing);

    // Polar ice brightness. Antarctica + Greenland in real ISS / Apollo
    // imagery read as brilliant white — the texture has them but they
    // get muted by the same Lambert + boost as everything else. We lift
    // the luminance of high-latitude bright pixels so the ice caps pop
    // against the surrounding ocean / land.
    //   icyZone   = 1 at lat ≥ 0.85 (poles), 0 below 0.55
    //   iceFactor = takes already-bright pixels and amplifies them
    vec3 spinAxis = length(uSpinAxis) > 0.5 ? normalize(uSpinAxis) : vec3(0.0, 1.0, 0.0);
    float lat = abs(dot(baseNormal, spinAxis));
    float icyZone = smoothstep(0.55, 0.85, lat);
    float dayLuma = dot(dayColor.rgb, lumaWeights);
    float iceFactor = pow(max(dayLuma - 0.55, 0.0), 1.4) * 4.5;
    dayLit += vec3(1.0, 1.0, 1.05) * iceFactor * icyZone * diffuse * 0.55;

    // Atmospheric haze (aerial perspective) across the disc. Real
    // Earth from orbit has a Rayleigh-blue lift everywhere on the
    // day side, not just at the silhouette — the atmospheric column
    // overhead scatters blue wavelengths back to the camera. At
    // zenith view (looking straight down) the column is minimal;
    // at grazing view (toward the limb) it grows steeply per the
    // Kasten-Young air mass formula and the surface reads
    // increasingly blue. This is what makes the disc feel like it
    // has a wet atmosphere over the dry geography rather than the
    // continents and oceans living in a vacuum.
    //
    // viewZenith = cos(angle between view ray and surface normal).
    // Air mass ≈ 1/(cos + 0.04) gives the standard atmospheric
    // optical depth, capped so the limb doesn't blow out.
    float viewZenith = max(dot(perturbedNormal, V), 0.0);
    float viewAirMass = 1.0 / max(viewZenith + 0.04, 0.04);
    // pathFactor: ramps to 1 at the limb, with a 0.12 FLOOR at zenith.
    // The floor is the key to the real-photo look: orbital imagery
    // (Apollo Blue Marble, ISS) shows a soft milky atmospheric veil
    // over the WHOLE day disc, not just the silhouette — even
    // looking straight down there's a full atmospheric column
    // scattering light back. The earlier version started at exactly
    // 0 at zenith, which left the disc centre reading crisp and
    // airless. The floor adds a gentle everywhere-haze; the
    // air-mass term still steepens it toward the limb.
    // Scaled by the share of the air that lies below the camera: exactly 1
    // from orbit, about a fifth from 2 km up (EARTH_OCEAN_WAVES_GLSL).
    float pathFactor = clamp((0.37 + (viewAirMass - 1.0) * 0.34) * earthAirBelowCamera(), 0.0, 1.0);
    // In-scatter veil colour. Research pass (Bruneton/Hillaire +
    // orbital-imagery analysis) flagged the old saturated sky-blue as
    // a top CGI tell — it reads like a pool/cartoon. Real path-radiance
    // over the disc is a DESATURATED milky blue-grey (Rayleigh blue
    // diluted by Mie scatter + cloud albedo), so the veil is pulled
    // toward grey while staying cool.
    vec3 skyScatter = vec3(0.50, 0.66, 0.86);
    // The in-scattered light also tracks the sun's illumination of
    // the column overhead. Multiply by diffuse so the night side
    // doesn't pick up phantom blue haze where there's no sunlight
    // to scatter.
    float hazeMask = pathFactor * smoothstep(-0.10, 0.25, baseSunFacing);
    // Lift toward sky-blue. 0.32 max blend at the limb; with the new
    // zenith floor the disc centre now picks up a gentle ~0.04 veil
    // so the whole day side reads soft and atmospheric instead of
    // crisp. A straight line across OKLab (oklab.js, the documented
    // exception to OKLCh): the veil adds its own light over the ground,
    // and an OKLCh arc from desert orange to this blue flipped through
    // pink and lime from one pixel to the next.
    vec3 hazeVeil = skyScatter * (0.55 + diffuse * 0.45);
    // The air under a cloud's shadow is in it too: the veil over shaded
    // ground loses the share that came from below the cloud base
    // (earthcloudtop.js cloudTopVeilShadow). The cloud's own veil, above it,
    // keeps all of it.
    vec3 veiled = oklabMix(dayLit, hazeVeil * (1.0 - ctShadowSun * cloudTopVeilShadow(viewZenith)), hazeMask * 0.50);
    // Up close the veil is the clear air's own light: single Rayleigh
    // scattering in the air between the camera and the ground
    // (earthAirBelowCamera), with the ground seen through it
    // (earthocean.js). The veil above was fitted to DSCOVR EPIC, a
    // top-of-atmosphere view that carries the aerosol of an average sky as
    // well; the streamed pages are corrected reflectance, aerosol taken out,
    // and with that veil over them a desert coast from 15 km took a lavender
    // cast and the sea a milky blue. This keeps the air's blue, and its rise
    // toward the horizon, and the photograph's colours under it, and hands
    // over with the albedo (closeAlbedo), so no globe view changes.
    if (closeAlbedo > 0.0) {
      float airMuV = max(dot(baseNormal, V), 0.02);
      float airMuS = max(baseSunFacing, 0.02);
      float airColumn = earthAirBelowCamera();
      vec3 airLight = earthRayleighPath(airColumn, airMuS, airMuV, dot(-sunDir, V)) * EARTH_LIGHT
                    * smoothstep(-0.10, 0.25, baseSunFacing) * (1.0 - ctShadowSun * cloudTopVeilShadow(viewZenith));
      veiled = mix(veiled, dayLit * earthRayleighTransmit(airColumn, airMuV) + airLight, closeAlbedo);
    }
    dayLit = veiled;

    // The cloud over the veiled ground, veiled on its own by the air above
    // its top (earthcloudtop.js cloudTopColour: white lit over its slope,
    // sky-blue on its shaded flank, warmed through the terminator). An OKLab
    // mix is linear, so veiling the two apart and then mixing is veiling
    // the mix, except that a high top keeps more of its own brightness.
    vec3 cloudColor = cloudTopColour(ct, ctSun, hazeVeil, hazeMask * 0.50);
    // Pseudo-volumetric thickness shading: the face of a cloud mass away from
    // the sun (thicker neighbour sunward, a hundred kilometres off) reads
    // darker grey. A function of place, so it is the same at every distance.
    cloudColor *= cloudMassShade(cloudsTexture, cloudUV, ctMap, ctSun, ctMapLod);
    // The carve's opacity: rims translucent over the sea, cores near opaque.
    // Hand coverage over to the in-atmosphere volumes, which composite on
    // top of this layer: A = Av + Ag*(1-Av) keeps total cover continuous.
    float cloudCover = earthCloudFallbackAlpha(ct.alpha, uEarthVolumeBlend);
    // The night side keeps its own validated law: the map's S-curve, which
    // keeps cirrus faint and pushes storm cores toward opaque, taken on the
    // cover in the map's units (clouds.r, the map itself once nothing under
    // the texel is resolved, the carved cells up close). It sets the grey
    // deck over the city lights and where the lightning can fire.
    float cloudSignal = smoothstep(0.0, 0.72, pow(clouds.r, 0.55));
    float nightCover = earthCloudFallbackAlpha(cloudSignal * cloudOpacity, uEarthVolumeBlend);
    float cloudBlend = cloudCover * dayFactor;
    // A straight OKLab mix (oklchMixNeutral's near-grey branch: the cloud end
    // has no hue of its own to rotate) whose lightness and chroma carry the
    // linear-light sum (earthcloudtop.js cloudComposite): partial cover adds
    // the two radiances.
    dayLit = cloudComposite(dayLit, cloudColor, cloudBlend);

    // E6 — city radiance, faded with the complementary daylight mask below.
    vec3 cityLights = vec3(0.0);
    vec3 cityGlow = vec3(0.0);
    vec3 nightLit = vec3(0.0);
    if (nightHere) {
      cityLights = nightL.light * 2.5;
      // City light spectral diversity. Real city lights from orbit are
      // not a uniform amber. Older low-pressure-sodium lighting reads
      // around 1900K (deep amber), modern LED conversions read
      // 4000-6500K (cool white-blue). Tokyo, Seoul, central European
      // capitals have largely converted to LED, while much of the US,
      // South America, Africa, and the Middle East still run sodium.
      // We modulate per-region via slow noise to break the
      // "everything-is-the-same-amber" look. OKLCh blend so the
      // intermediate cities pass through proper warm-to-cool color
      // temperature rather than a desaturated linear-RGB midpoint.
      vec3 warmSodium = vec3(1.00, 0.78, 0.45);
      vec3 coolLed    = vec3(0.78, 0.88, 1.00);
      vec3 cityTint = oklchMix(warmSodium, coolLed, smoothstep(0.40, 0.70, cityRegion));
      // Close in, where single towns are drawn, each light carries its own
      // sodium or LED tint (nightimagery.js) and the region only leans warm or
      // cool. A straight blend: the OKLCh arc between orange and blue-white
      // passes through green, and lit cities read pale green from orbit.
      vec3 cityTintNear = nightTintNear(smoothstep(0.40, 0.70, cityRegion));
      cityTint = mix(cityTint, cityTintNear, nightL.close);
      cityLights *= cityTint;
      // From far off cloud dims the lights under it. Close in, a cloud over a
      // city also glows with its light, a soft patch about as wide as the
      // cloud, where the dimming alone punched dark specks into every city
      // through the cloud tops' cells.
      cityLights *= 1.0 - clouds.r * mix(0.7, 0.85, nightL.close);
      cityGlow = nightL.coarse * 2.5 * cityTint;
      // Only over ground the data lights (its floor, 1 from afar): the coarse
      // read alone put a lit rim on the cloud kilometres round every town.
      cityLights += cityGlow * clouds.r * 0.45 * nightL.close * nightL.floor;
      // City-light TWINKLE. Real ISS / Apollo night passes show the lit
      // grid quietly scintillating — atmospheric seeing, individual city
      // power cycles, thin cloud drifting over — never a dead static
      // field. The trick to reading as living "twinkle" rather than a
      // uniform pulse is INDEPENDENT phase per light, at two spatial
      // scales so it stays alive at any zoom:
      //   - region scale (vUv*360): big slow sway, visible at orbital
      //     distance where individual clusters are sub-pixel.
      //   - per-cluster scale (vUv*1300): faster sparkle, each city
      //     twinkling on its own clock — reads up close.
      // Two decorrelated seeds + two frequencies give an organic shimmer
      // instead of a mechanical sine. Kept deliberately subtle (~±10%
      // peak, typically less since the two sines rarely align) so the
      // night looks alive without flashing. Runs on wall-clock time
      // so it twinkles at the same rate regardless of sim time-speed.
      // Gated by city luminance so the dark ocean stays perfectly dark.
      float cityLumen = dot(nightColor.rgb, vec3(0.299, 0.587, 0.114));
      float twSeedA = earthValueNoise(vUv * 360.0);
      float twSeedB = earthValueNoise(vUv * 1300.0 + vec2(19.3, 7.1));
      float twSlow = sin(time * 0.8 + twSeedA * 34.0);
      float twFast = sin(time * 3.4 + twSeedB * 61.0);
      // The twinkle's cells are tens of kilometres wide: close in they would
      // pulse whole districts at once, so it fades there.
      float twinkleAmt = (twSlow * 0.05 + twFast * 0.05) * smoothstep(0.03, 0.10, cityLumen) * (1.0 - 0.8 * nightL.close);
      cityLights *= mix(1.0, 1.0 + twinkleAmt, smoothstep(0.03, 0.12, cityLumen));
      // City light atmospheric halo. Real ISS night photographs show
      // each city cluster surrounded by a soft warm glow extending a
      // few pixels past the lit area itself — light from sodium / LED
      // streetlamps scatters sideways through the atmospheric column
      // above before leaving the planet, producing a haze around the
      // urban grid. Without this the lights read as too-sharp pinpoints.
      //
      // Two-ring hexagonal sampling: a tight 6-tap inner ring picks up
      // the soft bloom right next to a city, plus a 6-tap outer ring at
      // 2.4x radius for the wider glow around megacity clusters.
      //
      // CRITICAL: halo radius scales INVERSELY with camera distance.
      // At orbital overview the fixed UV offsets (0.0035 / 0.0085)
      // read as a few pixels of haze; at ISS-altitude close zoom those
      // same offsets span huge fractions of screen space and the
      // halo bloats into obvious smear patterns over each city. The
      // smoothstep dials the radius down to 15% at close zoom so the
      // halo stays bounded to "soft fringe" rather than "city-sized
      // blob," then ramps back to full at overview distance.
      float haloDistScale = mix(0.15, 1.0, smoothstep(2.5 * 1.2, 2.5 * 5.0, earthCamDist));
      float HALO_R1 = 0.0035 * haloDistScale;
      float HALO_R2 = 0.0085 * haloDistScale;
      vec3 haloInner = (
        texture2D(nightTexture, vUv + vec2( HALO_R1,        0.0)).rgb +
        texture2D(nightTexture, vUv + vec2( HALO_R1 * 0.5,  HALO_R1 * 0.866)).rgb +
        texture2D(nightTexture, vUv + vec2(-HALO_R1 * 0.5,  HALO_R1 * 0.866)).rgb +
        texture2D(nightTexture, vUv + vec2(-HALO_R1,        0.0)).rgb +
        texture2D(nightTexture, vUv + vec2(-HALO_R1 * 0.5, -HALO_R1 * 0.866)).rgb +
        texture2D(nightTexture, vUv + vec2( HALO_R1 * 0.5, -HALO_R1 * 0.866)).rgb
      ) * (1.0 / 6.0);
      vec3 haloOuter = (
        texture2D(nightTexture, vUv + vec2( HALO_R2,        0.0)).rgb +
        texture2D(nightTexture, vUv + vec2( HALO_R2 * 0.5,  HALO_R2 * 0.866)).rgb +
        texture2D(nightTexture, vUv + vec2(-HALO_R2 * 0.5,  HALO_R2 * 0.866)).rgb +
        texture2D(nightTexture, vUv + vec2(-HALO_R2,        0.0)).rgb +
        texture2D(nightTexture, vUv + vec2(-HALO_R2 * 0.5, -HALO_R2 * 0.866)).rgb +
        texture2D(nightTexture, vUv + vec2( HALO_R2 * 0.5, -HALO_R2 * 0.866)).rgb
      ) * (1.0 / 6.0);

      // Pull the halo luminance. Colour comes from the per-region
      // sodium/LED tint computed below for the actual city lights, so
      // the halo matches the colour palette of the local urban grid
      // (warm amber over LA, cool blue-white over Tokyo).
      float haloInnerLum = dot(haloInner, lumaWeights);
      float haloOuterLum = dot(haloOuter, lumaWeights);
      // Inner ring weighted 2x heavier than outer so the bloom drops
      // off naturally with distance.
      float haloLum = haloInnerLum * 0.65 + haloOuterLum * 0.35;
      // HARD smoothstep cutoff. The Earth night texture has faint
      // values across open ocean (atmospheric data, sensor noise,
      // baked-in scatter) that an earlier soft floor was lifting into
      // a visible blue-grey grain pattern over the Mediterranean and
      // other coastal seas. smoothstep(0.07, 0.18) means anything
      // below 7% luminance contributes nothing, only genuinely-lit
      // urban pixels get halos.
      haloLum = smoothstep(0.07, 0.18, haloLum) * haloLum;
      // Apply the same warm/cool city palette to the halo so a city
      // cluster's glow reads at the same colour temperature as the
      // lights themselves.
      vec3 haloGlow = haloLum * cityTint * 1.6;
      // Same cloud masking as the city lights — halo fades under
      // overcast just like real urban glow disappears beneath cumulus.
      haloGlow *= (1.0 - clouds.r * 0.7);

      // The halo's taps sample the 8K, which close in drew a smeared blob round
      // each city; there the glow comes from the coarse read of the lights
      // themselves, only round bright cores and only over ground the data
      // lights (its floor, nightimagery.js).
      vec3 closeGlow = cityGlow * smoothstep(0.04, 0.4, nightLuma(nightL.coarse)) * 0.14 * nightL.floor * nightL.haze;
      // Not on the sea by the shore: the coarse read spreads a city's light
      // kilometres offshore, a lit sheet over dark water close in.
      closeGlow *= 1.0 - nightSea.x * (1.0 - nightSea.y);
      nightLit = (cityLights * 0.6 + mix(haloGlow * 0.55, closeGlow, nightL.close)) * uNightLights;
      // Where the branch divides a 2 x 2 block the reads are wrong, but it
      // has no night or totality there; nothing of it shows, and nothing may
      // be a NaN (the NaN guard at the end would turn the pixel black).
      if ((nightMask <= 0.0 && eclipseHidden < 0.985)
          || any(notEqual(nightLit, nightLit)) || any(notEqual(cityGlow, cityGlow)) || any(notEqual(cityLights, cityLights))) {
        cityLights = vec3(0.0);
        cityGlow = vec3(0.0);
        nightLit = vec3(0.0);
        nightColor = vec4(0.0);
        nightL = earthNoNightLights();
      }
    }

    // Crossfade sunlight and city radiance without an additive twilight belt.
    // The extinction tint above already warms grazing sunlight.
    vec3 surfaceColor = dayLit * dayFactor + nightLit * nightMask;
    float surfaceLuma = dot(surfaceColor, lumaWeights);
    surfaceColor = mix(surfaceColor, vec3(surfaceLuma), 0.025);

    // Moonlit / starlit cloud deck on the night side: faint grey masses
    // occluding the city lights below them.
    float nightCloud = nightCover * nightMask;
    // Close in, the deck over a city is lit from below by it.
    vec3 nightDeck = vec3(0.040, 0.045, 0.060) * (0.6 + 0.4 * cloudSignal) + cityGlow * 0.25 * nightL.close * nightL.floor * uNightLights;
    surfaceColor = mix(surfaceColor, nightDeck, nightCloud * 0.55);

    // Night-side storm lightning. From orbit a flash lights a patch of its
    // storm's cloud tops from inside, a few tens of kilometres across, and the
    // light shows the cumulonimbus texture: bright domes over the updrafts,
    // darker between them (ISS photographs, NASA Earth Observatory "Lightning
    // Storm over Turkiye", 2023). It comes as a train of two to four return
    // strokes in half a second, then the storm is dark again for tens of
    // seconds.
    //
    // Rate. The whole Earth has about 44 flashes a second from a couple of
    // thousand thunderstorms, mostly over tropical land in the late
    // afternoon and evening (NASA LIS/OTD climatology: land ten times the
    // sea; diurnal peak 15 to 21 h local). Storm points sit jittered in cells
    // about 150 km across; a cell holds one only with a chance set by that
    // climate (land, the tropics or the summer hemisphere's mid-latitudes,
    // evening rather than before dawn), only under deep cloud, and each fires
    // every 12 to 45 s. A few hundred storms on the night side, a handful in
    // a view from the station, a flash every few seconds among them.
    //
    // The 3 x 3 cells round the pixel are searched, so a flash crosses cell
    // edges smoothly. Distances are metres on the surface, the lattice wraps
    // at the date line, and a flash is never drawn narrower than two pixels
    // (further off it widens and dims by the same area).
    //
    // Wall-clock time rather than uSimTime: at a fast time speed the sim-time
    // version strobes.
    //
    // NOTE: do not name a variable here 'active'. It is a reserved word in
    // GLSL ES 3.00 (WebGL2) though not in 1.00, and kills the Earth material.
    float stormMask = smoothstep(0.45, 0.85, cloudSignal) * smoothstep(0.15, 0.6, ct.core) * nightMask;
    if (stormMask > 0.003) {
      const float STORM_CELLS = 256.0;
      const float FLASH_RADIUS = 14000.0;
      vec2 grid = vec2(STORM_CELLS, STORM_CELLS * 0.5);
      vec2 cellUv = vUv * grid;
      vec2 base = floor(cellUv);
      float cosLat = cos((vUv.y - 0.5) * 3.14159265);
      vec2 cellM = vec2(40030000.0 / grid.x * max(cosLat, 0.05), 20015000.0 / grid.y);
      float radius = max(FLASH_RADIUS, 2.0 * ctPix);
      float energy = (FLASH_RADIUS * FLASH_RADIUS) / (radius * radius);
      // Summer hemisphere: the Sun's declination in the planet's frame.
      float sunDecl = sunLocal.y;
      float flash = 0.0;
      for (int j = -1; j <= 1; j++) {
        for (int i = -1; i <= 1; i++) {
          vec2 local = base + vec2(float(i), float(j));
          vec2 id = vec2(mod(local.x, grid.x), local.y);
          float h2 = earthHash(id + vec2(37.7, 12.3));
          float h3 = earthHash(id + vec2(5.1, 71.9));
          // Most of the time a cell is between flashes: that test comes first,
          // as it needs only the cell's clock.
          float period = 12.0 + 33.0 * h2;
          float cycle = time / period + h3 * 13.0;
          float sec = fract(cycle) * period;
          if (sec > 1.2) continue;
          float h = earthHash(id);
          vec2 centre = local + 0.1 + 0.8 * vec2(h2, h3);
          vec2 cUv = vec2(mod(centre.x, grid.x), centre.y) / grid;
          float latS = (cUv.y - 0.5) * 180.0;
          // Climate at the storm: the tropics, or the summer mid-latitudes.
          float tropics = exp(-pow(latS / 22.0, 2.0));
          float summer = smoothstep(20.0, 35.0, abs(latS)) * (1.0 - smoothstep(48.0, 60.0, abs(latS)))
                       * clamp(sign(latS) * sunDecl * 3.0, 0.0, 1.0);
          float climate = max(tropics, 0.45 * summer);
          if (climate < 0.02) continue;
          // Land ten times the sea.
          float land = 1.0 - (oceanTextureOn > 0.5 ? textureLod(oceanTexture, cUv, 6.0).r : 0.7);
          float landW = mix(0.1, 1.0, land);
          // Evening: the Sun set to the west of the storm a few hours ago.
          float sLat = (cUv.y - 0.5) * 3.14159265;
          float sLon = (cUv.x - 0.5) * 6.28318530718;
          vec3 sN = vec3(cos(sLat) * cos(sLon), sin(sLat), -cos(sLat) * sin(sLon));
          vec3 sE = normalize(vec3(sN.z, 0.0, -sN.x) + vec3(1e-5));
          float evening = 0.35 + 0.65 * smoothstep(0.2, -0.6, dot(sunLocal, sE));
          if (h > 0.22 * climate * landW * evening) continue;
          vec2 d = (cellUv - centre) * cellM;
          // Each flash comes from its own spot in the storm, up to ~12 km off.
          float epoch = floor(cycle);
          vec2 jitter = (vec2(earthHash(id + epoch), earthHash(id.yx + epoch * 1.7)) - 0.5) * 24000.0;
          float r2 = dot(d - jitter, d - jitter) / (radius * radius);
          if (r2 > 40.0) continue;
          float strokes = exp(-sec * 14.0)
                        + 0.65 * exp(-abs(sec - 0.09) * 50.0) * step(0.3, h2)
                        + 0.45 * exp(-abs(sec - 0.24) * 50.0) * step(0.55, h3)
                        + 0.30 * exp(-abs(sec - 0.41) * 50.0) * step(0.75, earthHash(id + epoch * 3.1));
          flash += strokes * (exp(-r2) + 0.06 * exp(-r2 * 0.15)) * energy;
        }
      }
      if (flash > 0.0) {
        // The cloud it lights gives the patch its texture: thick cores and
        // high turrets glow, the gaps and thin rims between them stay darker.
        // Under the cloud map's texel the storm top is drawn as updraft domes
        // a few kilometres across (bright crowns, dark troughs between them),
        // which fade to their mean once a pixel spans them; the patch's edge
        // is broken up by the same domes at a larger scale.
        vec2 stormM = vUv * vec2(40030000.0 * max(cosLat, 0.05), 20015000.0);
        float domes = earthValueNoise(stormM / 3500.0) * 0.6 + earthValueNoise(stormM / 1400.0 + 7.3) * 0.4;
        float domeLit = mix(0.55, smoothstep(0.3, 0.75, domes) * 0.85 + 0.15, 1.0 - smoothstep(700.0, 2500.0, ctPix));
        float edge = mix(0.75, smoothstep(0.25, 0.7, earthValueNoise(stormM / 9000.0 + 3.1)) * 0.8 + 0.2, 1.0 - smoothstep(3000.0, 9000.0, ctPix));
        float cloudLit = smoothstep(0.25, 0.95, ct.alpha) * (0.55 + 0.45 * ct.core)
                       * (0.7 + 0.3 * smoothstep(0.0, 1.0, ct.top / 12000.0)) * domeLit * edge;
        surfaceColor += vec3(0.78, 0.84, 1.0) * flash * stormMask * cloudLit * 1.6;
      }
    }



    // E4 — aerial perspective as ADDITIVE cyan-blue haze at the limb, not a
    // darkening mix. Every orbit photo (Apollo, ISS, Artemis) shows a bright
    // scattered-light halo where the view ray grazes the atmosphere — the
    // atmosphere is lit, it doesn't dim the surface. Gate by dayFactor so the
    // night limb stays clean.
    // EARTH_LIMB_HAZE (earthocean.js) was 0.6 here; see there.
    float aerial = pow(1.0 - max(dot(V, baseNormal), 0.0), 2.5);
    vec3 hazeColor = vec3(0.62, 0.78, 0.95);
    surfaceColor += hazeColor * aerial * EARTH_LIMB_HAZE * dayFactor;

    // Night-limb city-light scatter. ISS night photos show a faint
    // amber glow above the silhouette where city lights from BENEATH
    // the limb scatter through the atmosphere into our sightline.
    // Modulated by the night texture's local brightness so dark
    // regions (Sahara, oceans) don't fake-glow.
    float nightLimb = pow(1.0 - max(dot(V, baseNormal), 0.0), 4.0);
    float nightCityLumen = dot(nightColor.rgb, lumaWeights);
    vec3 cityHaze = vec3(1.0, 0.78, 0.40);
    surfaceColor += cityHaze * nightLimb * nightCityLumen * nightMask * 0.85;

    // Cyan twilight limb arc. On Earth's night side, the upper
    // atmosphere is still sunlit from below and Rayleigh-scatters
    // shortwavelengths back down — ISS / Apollo photos show a thin
    // electric-blue arc above the dark limb just inside the
    // terminator. Strongest where the view ray grazes the limb
    // (high fresnel) and the surface is in twilight (sunFacing near
    // zero, slightly negative), fading into the deep-night
    // hemisphere. This is the single most "real" terminator detail
    // a casual orbit shot has and it was completely missing.
    float twilightArc = exp(-pow((baseSunFacing + 0.04) / 0.08, 2.0));
    vec3 nightArcTint = vec3(0.30, 0.55, 0.95);
    surfaceColor += nightArcTint * nightLimb * twilightArc * 0.18;

    float moonTransitShadow = 1.0;
    if (uEclipseOn > 0.5) {
      // The light left is the Sun's disc left, with a little skylight from
      // the sunlit country round the umbra so the ground under totality
      // stays a deep twilight rather than black.
      moonTransitShadow = 1.0 - eclipseHidden * (1.0 - uEclipseSkyFloor);
    }
    // Saturn's rings round the Earth (?whatif=rings, src/earthrings.js): the
    // light they let through, with the same floor of skylight as an eclipse.
    moonTransitShadow *= 1.0 - (1.0 - earthRingLight(vWorldPosition)) * 0.975;
    for (int i = 0; i < 8; i++) {
      if (uEclipseOn > 0.5 || i >= moonShadowCount) break;
      vec3 moonPos = moonShadowPositions[i];
      float moonDistance = length(moonPos);
      if (moonDistance > 0.0001) {
        vec3 shadowAxis = moonPos / moonDistance;
        vec3 moonToFrag = vWorldPosition - moonPos;
        float t = dot(moonToFrag, shadowAxis);
        vec3 perp = moonToFrag - shadowAxis * t;
        float radius = moonShadowRadii[i];
        // Two-band shadow: tight inner umbra (~5% brightness in the
        // path of totality) wrapped by a softer penumbra fade. Real
        // total-eclipse imagery from ISS shows a clear dark spot
        // surrounded by a wider grey halo; previous single-zone
        // 0.76 darkening read as a fuzzy patch rather than a real
        // umbra.
        float umbraDist = length(perp);
        // Three-zone eclipse shadow modelled after real Apollo /
        // DSCOVR imagery. Real solar-eclipse shadows on Earth show:
        //   - tiny umbra spot (~100 km, dark) — physically correct
        //   - VERY wide penumbra ring (thousands of km, soft fade)
        //   - antumbral fringe extending much further still
        // Previously we had a narrow penumbra (0.85→1.45 radius) so
        // the shadow read as a sharp dot. Widened the penumbra
        // dramatically (0.6→4.0 radius) and split into an inner
        // penumbra (visible darkening) and an outer fringe (barely
        // perceptible tint) so the shadow has the soft-fading
        // character of the real thing.
        float umbra        = 1.0 - smoothstep(radius * 0.35, radius * 0.60, umbraDist);
        float innerPenumbra = 1.0 - smoothstep(radius * 0.60, radius * 1.80, umbraDist);
        float outerPenumbra = 1.0 - smoothstep(radius * 1.80, radius * 4.00, umbraDist);
        // Darkening multipliers reduced because the previous 0.88
        // umbra was producing near-black in the eclipse center, which
        // exaggerates the visual impact relative to real DSCOVR-EPIC
        // eclipse imagery (where the moon's shadow shows as a clearly
        // dark but still detail-readable disc, not an inky void).
        // Earth's air-mass terminator and ambient lift since landed
        // pulled the overall surface brighter, so the old shadow
        // strength now reads as too aggressive. New values:
        //   umbra 0.62 — clearly dim, terrain still visible
        //   inner penumbra 0.20 — softer dimming ring
        //   outer 0.06 — barely perceptible fringe
        moonTransitShadow *= 1.0 - umbra * step(0.0, t) * 0.62;
        moonTransitShadow *= 1.0 - (innerPenumbra - umbra) * step(0.0, t) * 0.20;
        moonTransitShadow *= 1.0 - (outerPenumbra - innerPenumbra) * step(0.0, t) * 0.06;
      }
    }

    // The Moon's visible transit shadow only belongs on the sunlit
    // hemisphere. Applying it after clouds/haze keeps the whole visible
    // column darkened, like eclipse photos from orbit.
    float shadowVisibility = smoothstep(0.02, 0.18, baseSunFacing);
    surfaceColor *= mix(1.0, moonTransitShadow, shadowVisibility);
    // Under totality it is as dark as late twilight and streetlights on
    // light sensors come on: a faint trace of the city lights in the umbra.
    float umbraNight = smoothstep(0.985, 1.0, eclipseHidden) * shadowVisibility;
    surfaceColor += cityLights * 0.06 * umbraNight * uNightLights;

    // Final sanitation. Any NaN leaking through (from texture sampling, pow,
    // or division corner cases) becomes black under bloom. NaN != NaN in
    // IEEE, so notEqual catches it; any() collapses to a scalar so we can
    // replace the whole colour with zero atomically.
    if (any(notEqual(surfaceColor, surfaceColor))) {
      surfaceColor = vec3(0.0);
    }
    gl_FragColor = vec4(max(surfaceColor, vec3(0.0)), 1.0);
  }
`;
